import React, { useState, useEffect } from 'react';
import { statsAPI } from '../services/api';
import './Statistics.css';

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('deviceType');
  const [multipleDevicesData, setMultipleDevicesData] = useState([]);
  const [loadingMultipleDevices, setLoadingMultipleDevices] = useState(false);
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('');
  const [expandedEmployees, setExpandedEmployees] = useState({});

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'multipleDevices' && multipleDevicesData.length === 0) {
      loadMultipleDevicesData();
    }
  }, [activeTab]);

  const loadStats = async () => {
    try {
      const response = await statsAPI.getAll();
      console.log('Statistics data:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMultipleDevicesData = async () => {
    try {
      setLoadingMultipleDevices(true);
      const response = await statsAPI.getEmployeesWithMultipleDevices();
      console.log('Multiple devices data:', response.data);
      setMultipleDevicesData(response.data);
    } catch (error) {
      console.error('Failed to load multiple devices data:', error);
    } finally {
      setLoadingMultipleDevices(false);
    }
  };

  const toggleEmployeeDevices = (employeeId) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  if (loading) {
    return (
      <div className="statistics-container">
        <div className="statistics-header">
          <h1>Statistics</h1>
        </div>
        <div className="card">
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="statistics-container">
        <div className="statistics-header">
          <h1>Statistics</h1>
        </div>
        <div className="card">
          <p>Failed to load statistics</p>
        </div>
      </div>
    );
  }

  // Helper function to format table data
  const formatDUTable = (statsByDU, columns) => {
    const rows = [];
    Object.keys(statsByDU).sort().forEach(du => {
      Object.keys(statsByDU[du]).sort().forEach(key => {
        const data = statsByDU[du][key];
        rows.push({
          du,
          key,
          ...data
        });
      });
    });
    return rows;
  };

  const tabs = [
    { id: 'deviceType', label: 'Device Type', icon: '📱' },
    { id: 'osVersion', label: 'OS Version', icon: '💿' },
    { id: 'model', label: 'Model', icon: '🔧' },
    { id: 'teamId', label: 'Team ID', icon: '👥' },
    { id: 'multipleDevices', label: 'Multiple Devices', icon: '📊' }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'deviceType':
        return (
          <div className="card statistics-card">
            <div className="statistics-card-header">
              <h2>Devices by Delivery Unit & Device Type</h2>
              <p className="card-description">Total count of devices grouped by delivery unit and device type, showing usage and condition status</p>
            </div>
            {stats.statsByDUAndDeviceType && Object.keys(stats.statsByDUAndDeviceType).length > 0 ? (
              <div className="table-container">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th>Delivery Unit</th>
                      <th>Device Type</th>
                      <th>Total</th>
                      <th>Used</th>
                      <th>Free</th>
                      <th>Bad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formatDUTable(stats.statsByDUAndDeviceType).map((row, idx) => (
                      <tr key={idx}>
                        <td className="du-cell">{row.du}</td>
                        <td>{row.key}</td>
                        <td className="number-cell">{row.total}</td>
                        <td className="number-cell status-used">{row.used}</td>
                        <td className="number-cell status-free">{row.free}</td>
                        <td className="number-cell status-bad">{row.bad}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No device type data available</p>
            )}
          </div>
        );

      case 'osVersion':
        return (
          <div className="card statistics-card">
            <div className="statistics-card-header">
              <h2>Devices by Delivery Unit & OS Version</h2>
              <p className="card-description">Total count of devices grouped by delivery unit and operating system version</p>
            </div>
            {stats.statsByDUAndOS && Object.keys(stats.statsByDUAndOS).length > 0 ? (
              <div className="table-container">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th>Delivery Unit</th>
                      <th>OS Version</th>
                      <th>Total</th>
                      <th>Used</th>
                      <th>Free</th>
                      <th>Bad</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formatDUTable(stats.statsByDUAndOS).map((row, idx) => (
                      <tr key={idx}>
                        <td className="du-cell">{row.du}</td>
                        <td>{row.key}</td>
                        <td className="number-cell">{row.total}</td>
                        <td className="number-cell status-used">{row.used}</td>
                        <td className="number-cell status-free">{row.free}</td>
                        <td className="number-cell status-bad">{row.bad}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No OS version data available</p>
            )}
          </div>
        );

      case 'model':
        return (
          <div className="card statistics-card">
            <div className="statistics-card-header">
              <h2>Devices by Delivery Unit & Model</h2>
              <p className="card-description">Total count of devices grouped by delivery unit and device model</p>
            </div>
            {stats.statsByDUAndModel && Object.keys(stats.statsByDUAndModel).length > 0 ? (
              <div className="table-container">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th>Delivery Unit</th>
                      <th>Model</th>
                      <th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formatDUTable(stats.statsByDUAndModel).map((row, idx) => (
                      <tr key={idx}>
                        <td className="du-cell">{row.du}</td>
                        <td>{row.key}</td>
                        <td className="number-cell">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No model data available</p>
            )}
          </div>
        );

      case 'teamId':
        const deviceTypes = stats.allDeviceTypes || [];
        return (
          <div className="card statistics-card">
            <div className="statistics-card-header">
              <h2>Devices by Registered Team ID</h2>
              <p className="card-description">Total count of devices grouped by registered team identifier with device type breakdown</p>
            </div>
            {stats.statsByRegisteredTeamId && Object.keys(stats.statsByRegisteredTeamId).length > 0 ? (
              <div className="table-container">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th>Registered Team ID</th>
                      {deviceTypes.map(type => (
                        <th key={type} className="device-type-column">{type}</th>
                      ))}
                      <th className="total-column">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(stats.statsByRegisteredTeamId)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([teamId, data]) => (
                        <tr key={teamId}>
                          <td className="team-id-cell">{teamId}</td>
                          {deviceTypes.map(type => (
                            <td key={type} className="number-cell">
                              {data.byDeviceType[type] || 0}
                            </td>
                          ))}
                          <td className="number-cell total-cell">{data.total}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No registered team ID data available</p>
            )}
          </div>
        );

      case 'multipleDevices':
        // Get all unique device types for filter
        const allDeviceTypes = [...new Set(
          multipleDevicesData.flatMap(emp => 
            emp.devices.map(d => d.deviceType)
          )
        )].sort();

        // Apply device type filter - show only employees with 2+ devices of the selected type
        const filteredEmployees = deviceTypeFilter 
          ? multipleDevicesData
              .map(emp => {
                const devicesOfType = emp.devices.filter(d => d.deviceType === deviceTypeFilter);
                return {
                  ...emp,
                  devices: devicesOfType,
                  totalDevices: devicesOfType.length
                };
              })
              .filter(emp => emp.totalDevices > 1) // Only show if more than 1 device of this type
          : multipleDevicesData;

        return (
          <div className="card statistics-card">
            <div className="statistics-card-header">
              <h2>Employees with Multiple Devices</h2>
              <p className="card-description">
                List of employees assigned to 2 or more devices with detailed device information
              </p>
              
              {/* Device Type Filter */}
              <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <label htmlFor="deviceTypeFilter" style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>
                  Filter by Device Type:
                </label>
                <select
                  id="deviceTypeFilter"
                  value={deviceTypeFilter}
                  onChange={(e) => setDeviceTypeFilter(e.target.value)}
                  style={{
                    padding: '0.4rem 0.8rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '0.9rem'
                  }}
                >
                  <option value="">All Device Types</option>
                  {allDeviceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
                {deviceTypeFilter && (
                  <button
                    onClick={() => setDeviceTypeFilter('')}
                    style={{
                      padding: '0.4rem 0.8rem',
                      background: '#f3f4f6',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    Clear Filter
                  </button>
                )}
              </div>
            </div>
            
            {loadingMultipleDevices ? (
              <p>Loading employees with multiple devices...</p>
            ) : filteredEmployees.length > 0 ? (
              <div className="table-container">
                <table className="statistics-table">
                  <thead>
                    <tr>
                      <th style={{ width: '30px' }}></th>
                      <th>Employee Name</th>
                      <th>Email</th>
                      <th>Delivery Unit</th>
                      <th>Total Devices</th>
                      <th>Device Types</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((emp, idx) => (
                      <React.Fragment key={emp.employeeId}>
                        <tr 
                          style={{ cursor: 'pointer', background: expandedEmployees[emp.employeeId] ? '#f9fafb' : 'transparent' }}
                          onClick={() => toggleEmployeeDevices(emp.employeeId)}
                        >
                          <td style={{ textAlign: 'center' }}>
                            <span style={{ fontSize: '1.2rem' }}>
                              {expandedEmployees[emp.employeeId] ? '▼' : '▶'}
                            </span>
                          </td>
                          <td>{emp.employeeName}</td>
                          <td>{emp.employeeEmail}</td>
                          <td>{emp.deliveryUnit || 'N/A'}</td>
                          <td className="number-cell">{emp.totalDevices}</td>
                          <td>
                            {Object.entries(emp.deviceTypeCount)
                              .map(([type, count]) => `${type} (${count})`)
                              .join(', ')}
                          </td>
                        </tr>
                        {expandedEmployees[emp.employeeId] && (
                          <tr>
                            <td colSpan="6" style={{ padding: '1rem', background: '#f9fafb' }}>
                              <div style={{ marginLeft: '2rem' }}>
                                <h4 style={{ marginBottom: '0.5rem', fontSize: '0.95rem', color: '#374151' }}>
                                  Device Details:
                                </h4>
                                <table style={{ width: '100%', fontSize: '0.9rem' }}>
                                  <thead>
                                    <tr style={{ background: '#e5e7eb' }}>
                                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Asset ID</th>
                                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Device Name</th>
                                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Device Type</th>
                                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Model</th>
                                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>OS Version</th>
                                      <th style={{ padding: '0.5rem', textAlign: 'left' }}>Delivery Unit</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {emp.devices.map((device, deviceIdx) => (
                                      <tr key={deviceIdx} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                        <td style={{ padding: '0.5rem' }}>{device.assetId}</td>
                                        <td style={{ padding: '0.5rem' }}>{device.deviceName}</td>
                                        <td style={{ padding: '0.5rem' }}>{device.deviceType}</td>
                                        <td style={{ padding: '0.5rem' }}>{device.model}</td>
                                        <td style={{ padding: '0.5rem' }}>{device.osVersion || 'N/A'}</td>
                                        <td style={{ padding: '0.5rem' }}>{device.deliveryUnit || 'N/A'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6b7280' }}>
                  <strong>Total: </strong>
                  {filteredEmployees.length} employee{filteredEmployees.length !== 1 ? 's' : ''} with multiple devices
                  {deviceTypeFilter && ` (filtered by ${deviceTypeFilter})`}
                </div>
              </div>
            ) : (
              <p className="no-data">
                {deviceTypeFilter 
                  ? `No employees found with multiple ${deviceTypeFilter} devices`
                  : 'No employees with multiple devices found'
                }
              </p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="statistics-container">
      <div className="statistics-header">
        <h1>Statistics</h1>
        <p className="statistics-subtitle">Comprehensive device analytics and reports</p>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default Statistics;
